// components/keyboard/ShortcutProvider.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { shortcutManager } from './shortcutManager';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { ShortcutContext as ShortcutContextType, ShortcutConfig } from './types';

const ShortcutContext = createContext<ShortcutContextType | undefined>(undefined);

export const useShortcuts = () => {
  const context = useContext(ShortcutContext);
  if (!context) {
    throw new Error('useShortcuts must be used within a ShortcutProvider');
  }
  return context;
};

interface ShortcutProviderProps {
  children: React.ReactNode;
}

export const ShortcutProvider: React.FC<ShortcutProviderProps> = ({ children }) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  useKeyboardShortcuts();

  useEffect(() => {
    const handleHelpStateChange = (isOpen: boolean) => {
      setIsHelpOpen(isOpen);
    };

    shortcutManager.onHelpStateChanged(handleHelpStateChange);
    
    // Register help shortcut with special handling
    const handleQuestionMark = (event: KeyboardEvent) => {
      // Check if ? is pressed (Shift + / on US keyboards)
      if (event.key === '?' || (event.shiftKey && event.key === '/')) {
        // Don't trigger if typing in input
        const target = event.target as HTMLElement;
        const isInput = target.tagName === 'INPUT' || 
                       target.tagName === 'TEXTAREA' || 
                       target.isContentEditable;
        
        if (!isInput) {
          console.log('🎯 Help shortcut triggered!');
          event.preventDefault();
          event.stopPropagation();
          setIsHelpOpen(true);
        }
      }
    };

    // Register escape key to close help
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isHelpOpen) {
        console.log('🎯 Closing help modal');
        event.preventDefault();
        event.stopPropagation();
        setIsHelpOpen(false);
      }
    };

    window.addEventListener('keydown', handleQuestionMark, { capture: true });
    window.addEventListener('keydown', handleEscape, { capture: true });

    return () => {
      window.removeEventListener('keydown', handleQuestionMark, { capture: true });
      window.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [isHelpOpen]);

  const contextValue: ShortcutContextType = {
    registerShortcut: (config: ShortcutConfig) => shortcutManager.registerShortcut(config),
    unregisterShortcut: (id: string) => shortcutManager.unregisterShortcut(id),
    triggerShortcut: (id: string) => shortcutManager.triggerShortcut(id),
    showHelp: () => {
      console.log('📖 Opening help modal');
      setIsHelpOpen(true);
    },
    hideHelp: () => {
      console.log('📖 Closing help modal');
      setIsHelpOpen(false);
    },
    isHelpOpen,
  };

  return (
    <ShortcutContext.Provider value={contextValue}>
      {children}
    </ShortcutContext.Provider>
  );
};